import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../api/auth";
import { getSnapshot, type SnapshotEnrollment } from "../api/snapshot";
import { getCourses, type Course } from "../api/courses";
import { APP_CONFIG } from "../utils/api";

type CourseState = {
  isOwned: boolean;
  isCompleted: boolean;
  hasTaskToday: boolean;
};

function computeStartOfCourseDay(now: Date, tz: string, h: number, m: number): Date {
  try {
    // Шаг 1: текущая дата (Y/M/D) в таймзоне пользователя
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
    const y = parseInt(parts.find(p => p.type === "year")!.value, 10);
    const mo = parseInt(parts.find(p => p.type === "month")!.value, 10);
    const d = parseInt(parts.find(p => p.type === "day")!.value, 10);

    // Шаг 2: UTC-кандидат "эта дата в h:m UTC"
    const utcCandidate = new Date(Date.UTC(y, mo - 1, d, h, m, 0));

    // Шаг 3: чему соответствует этот UTC-момент в TZ пользователя
    const tzParts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(utcCandidate);
    const tzH = parseInt(tzParts.find(p => p.type === "hour")!.value, 10);
    const tzM = parseInt(tzParts.find(p => p.type === "minute")!.value, 10);

    // Шаг 4: смещение = разница желаемого и фактического TZ-времени
    const diffMs = ((h * 60 + m) - (tzH * 60 + tzM)) * 60 * 1000;
    const result = new Date(utcCandidate.getTime() + diffMs);

    // Шаг 5: если сейчас до этой точки — вчерашний старт
    if (now < result) result.setUTCDate(result.getUTCDate() - 1);

    return result;
  } catch {
    // Fallback: h:m по локальному времени браузера
    const fallback = new Date();
    fallback.setHours(h, m, 0, 0);
    if (now < fallback) fallback.setDate(fallback.getDate() - 1);
    return fallback;
  }
}

function getCourseState(
  course: Course,
  enrollment: SnapshotEnrollment | undefined,
  tz: string | null
): CourseState {
  if (!enrollment) return { isOwned: false, isCompleted: false, hasTaskToday: false };
  const isCompleted = enrollment.current_day >= course.duration_days;
  if (isCompleted) return { isOwned: true, isCompleted: true, hasTaskToday: false };

  const userTz = tz || "Europe/Moscow";
  const now = new Date();
  const morningHour = enrollment.morning_hour_effective ?? course.morning_hour;
  const morningMinute = enrollment.morning_minute_effective ?? course.morning_minute;
  const startOfCourseDay = computeStartOfCourseDay(now, userTz, morningHour, morningMinute);
  const lastComplete = enrollment.last_complete_at
    ? new Date(enrollment.last_complete_at)
    : null;
  const hasTaskToday = !lastComplete || lastComplete < startOfCourseDay;

  return { isOwned: true, isCompleted: false, hasTaskToday };
}

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat("ru-RU", { style: "currency", currency }).format(price);
  } catch {
    return `${price} ${currency}`;
  }
}

const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrollments, setEnrollments] = useState<SnapshotEnrollment[]>([]);
  const [profileTz, setProfileTz] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // M5-STEP1 (15.09.2026): каталог различает "пусто" от "не удалось загрузить" —
  // раньше любой сбой /api/courses молча давал courses=[], неотличимо от
  // легитимно пустого каталога (SILENT-ERRORS/M5-COLDSTART-RECON, находка «в»).
  const [catalogError, setCatalogError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);

        const catalogResult = await getCourses();
        if (!cancelled) {
          if (catalogResult.status === "ok" || catalogResult.status === "cached") {
            // "cached" — офлайн/сбой сети, последний известный каталог;
            // витрина рисует курсы из него так же, как из живого ответа.
            setCourses(catalogResult.data);
            setCatalogError(false);
          } else if (catalogResult.status === "empty") {
            setCourses([]);
            setCatalogError(false);
          } else {
            setCourses([]);
            setCatalogError(true);
          }
        }
        if (cancelled) return;

        if (auth.isAuthenticated()) {
          const result = await getSnapshot();
          if (!cancelled) {
            if (result.status === "ok" || result.status === "cached") {
              // M5-STORE1: "cached" — офлайн, последний известный снимок; витрина
              // рисует прогресс из него так же, как из живого ответа.
              setEnrollments(result.data.enrollments ?? []);
              setProfileTz(result.data.profile?.timezone ?? null);
              setLoadError(false);
            } else if (result.status === "empty") {
              // легитимно "не авторизован"/нет снапшота — не отказ, без баннера
              setLoadError(false);
            } else {
              setLoadError(true);
            }
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const sorted = useMemo(() => {
    const isAuth = auth.isAuthenticated();
    const enrMap = new Map(enrollments.map(e => [Number(e.course_id), e]));
    return [...courses]
      .map(c => ({ course: c, state: getCourseState(c, enrMap.get(c.id), profileTz) }))
      .sort((a, b) => {
        if (isAuth) {
          if (a.state.hasTaskToday !== b.state.hasTaskToday)
            return a.state.hasTaskToday ? -1 : 1;
          if (a.state.isOwned !== b.state.isOwned)
            return a.state.isOwned ? -1 : 1;
        }
        const sa = a.course.sort ?? 999999;
        const sb = b.course.sort ?? 999999;
        if (sa !== sb) return sa - sb;
        return a.course.id - b.course.id;
      });
  }, [courses, enrollments, profileTz]);

  if (loading) {
    return (
      <div className="min-h-screen bg-app px-4 py-8 text-center">
        Загрузка...
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="min-h-screen bg-app px-4 py-8 text-center">
        {catalogError ? (
          <div className="mb-4 rounded-xl bg-amber-50 text-amber-900 px-4 py-3 text-sm text-center">
            Не удалось загрузить каталог. Проверьте соединение.
          </div>
        ) : (
          "Курсы пока не найдены."
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app px-4 py-8">
      <h1 className="text-3xl font-bold mb-6 text-center">Добро пожаловать в {APP_CONFIG.brandName}!</h1>
      {loadError && auth.isAuthenticated() && (
        <div className="mb-4 rounded-xl bg-amber-50 text-amber-900 px-4 py-3 text-sm text-center">
          Не удалось загрузить ваш прогресс. Проверьте соединение и обновите страницу.
        </div>
      )}
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {sorted.map(({ course, state }) => {
          const description = course.short_description ?? course.description ?? "";
          const isClickable = state.isOwned || Boolean(course.landing_url);

          function handleCardClick() {
            if (state.isOwned) {
              navigate(`/courses/${course.id}`);
            } else if (course.landing_url) {
              window.location.href = course.landing_url;
            }
          }

          return (
            <div
              key={course.id}
              className={
                "bg-white rounded-2xl shadow overflow-hidden flex flex-col relative" +
                (isClickable ? " cursor-pointer hover:shadow-lg transition" : "")
              }
              onClick={isClickable ? handleCardClick : undefined}
            >
              {/* Обложка */}
              {course.cover_image_url ? (
                <img
                  src={course.cover_image_url}
                  alt={course.title}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-pink-200 to-purple-300 flex items-center justify-center">
                  <span className="text-6xl font-bold text-white opacity-80 select-none">
                    {course.title.charAt(0)}
                  </span>
                </div>
              )}

              {/* Индикатор незавершённого задания */}
              {state.hasTaskToday && (
                <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-red-500 text-white text-sm font-bold flex items-center justify-center shadow">
                  !
                </div>
              )}

              {/* Контент */}
              <div className="p-4 flex flex-col flex-1">
                <h2 className="text-xl font-semibold mb-1">{course.title}</h2>
                {course.duration_days > 0 && (
                  <p className="text-xs text-gray-400 mb-2">{course.duration_days} дней</p>
                )}
                {description && (
                  <div
                    className="text-gray-600 text-sm mb-4 flex-1"
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                )}

                <div className="mt-auto flex items-center justify-between gap-2 flex-wrap">
                  {!state.isOwned && (
                    <span className="text-gray-800 font-semibold text-sm">
                      {formatPrice(course.price, course.currency)}
                    </span>
                  )}

                  {!state.isOwned && course.landing_url && (
                    <button
                      className="btn-cta rounded-lg py-2 px-4 font-bold"
                      onClick={e => {
                        e.stopPropagation();
                        window.location.href = course.landing_url!;
                      }}
                    >
                      Купить
                    </button>
                  )}

                  {!state.isOwned && !course.landing_url && (
                    <button
                      disabled
                      className="bg-gray-300 text-gray-500 rounded-lg py-2 px-4 font-bold cursor-default ml-auto"
                    >
                      Скоро
                    </button>
                  )}

                  {state.isOwned && state.isCompleted && (
                    <button
                      className="btn-primary rounded-lg py-2 px-4 font-bold ml-auto"
                      onClick={e => {
                        e.stopPropagation();
                        navigate(`/courses/${course.id}`);
                      }}
                    >
                      Открыть
                    </button>
                  )}

                  {state.isOwned && !state.isCompleted && (
                    <button
                      className="btn-primary rounded-lg py-2 px-4 font-bold ml-auto"
                      onClick={e => {
                        e.stopPropagation();
                        navigate(`/courses/${course.id}`);
                      }}
                    >
                      Продолжить
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HomeScreen;
